/**
 * A table of shipments. Rendered when the "My shipments" page view is set to
 * "table".
 */

import React, { useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import Table, { TableRow, TableCell } from "@amzn/meridian/table"
import NetworkLoadSlaTableRow from "./network-load-sla-table-row"
import dockReceivedPropType from "../../prop-types/dock-received"
import NetworkSLATableColumns from "../../data/network-sla-table-columns"

// Optimized sorting functions
const sortingFunctions = {
    timeToSLA: (a, b, direction) => {
        const x = a.lastDockReceiveTime + (a.sla ? Math.min(a.sla, 24) : 24) * 60 * 60 * 1000
        const y = b.lastDockReceiveTime + (b.sla ? Math.min(b.sla, 24) : 24) * 60 * 60 * 1000
        const result = x - y
        return direction === "ascending" ? -result : result
    },
    timePastSLA: (a, b, direction) => {
        const now = Date.now()
        const aSlaDeadline = a.lastDockReceiveTime + (a.sla ? Math.min(a.sla, 24) : 24) * 60 * 60 * 1000
        const bSlaDeadline = b.lastDockReceiveTime + (b.sla ? Math.min(b.sla, 24) : 24) * 60 * 60 * 1000
        const aTimePast = now - aSlaDeadline
        const bTimePast = now - bSlaDeadline
        const result = aTimePast - bTimePast
        return direction === "ascending" ? -result : result
    },
    dwellDays: (a, b, direction) => {
        const now = Date.now()
        const aDwell = (now - a.lastDockReceiveTime) / (24 * 60 * 60 * 1000)
        const bDwell = (now - b.lastDockReceiveTime) / (24 * 60 * 60 * 1000)
        const result = aDwell - bDwell
        return direction === "ascending" ? -result : result
    },
    progress: (a, b, direction) => {
        const aProgress = a.receivedUnits / a.confirmedUnits
        const bProgress = b.receivedUnits / b.confirmedUnits
        const result = aProgress - bProgress
        return direction === "ascending" ? -result : result
    },
    nyrnys: (a, b, direction) => {
        const aNyr = a.confirmedUnits - a.receivedUnits
        const bNyr = b.confirmedUnits - b.receivedUnits
        const result = aNyr - bNyr
        return direction === "ascending" ? -result : result
    },
    default: (a, b, direction, column) => {
        const aVal = a[column]
        const bVal = b[column]
        if (aVal < bVal) return direction === "ascending" ? -1 : 1
        if (aVal > bVal) return direction === "ascending" ? 1 : -1
        return 0
    }
}

const NetworkLoadSlaTable = props => {
    const { dockReceived, visibleIndices } = props
    const [sortColumn, setSortColumn] = useState("")
    const [sortDirection, setSortDirection] = useState("ascending")

    const onSort = useCallback(({ sortColumn, sortDirection }) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, [])

    // Memoized sorting logic
    const sortedNetworkSLA = useMemo(() => {
        if (!dockReceived || !sortColumn) {
            return dockReceived || []
        }

        const sortedData = [...dockReceived]
        const sortFn = sortingFunctions[sortColumn] || sortingFunctions.default

        return sortedData.sort((a, b) => sortFn(a, b, sortDirection, sortColumn))
    }, [dockReceived, sortColumn, sortDirection])

    // Memoized visible data slice
    const visibleNetworkSLA = useMemo(() => {
        return sortedNetworkSLA.slice(visibleIndices[0], visibleIndices[1])
    }, [sortedNetworkSLA, visibleIndices])

    return (
        <React.Fragment>
            <Table
                headerRows={1}
                // layout={"fixed"}
                showDividers={true}
                rowComponents={[NetworkLoadSlaTableRow, TableRow]}
                stickyHeaderRow={true}
                stickyHeaderColumn={false}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                showStripes={true}
            >
                <TableRow>
                    {NetworkSLATableColumns
                        .map(header => (
                            <TableCell
                                key={header.id}
                                sortColumn={header.id}
                                width={header.width}
                                alignmentHorizontal={header.alignment}
                            >
                                {header.label}
                            </TableCell>
                        ))}
                    {/* <TableCell alignmentHorizontal="center"><Text><strong>Action</strong></Text></TableCell> */}
                </TableRow>
                {visibleNetworkSLA.map((dockReceived, index) => (
                    <NetworkLoadSlaTableRow
                        key={dockReceived.id}
                        dockReceived={dockReceived}
                    />
                ))}
            </Table>
        </React.Fragment>
    )
}

NetworkLoadSlaTable.propTypes = {
    visibleIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
    dockReceived: PropTypes.arrayOf(dockReceivedPropType),
}

NetworkLoadSlaTable.defaultProps = {
    areAllSelected: false,
}

export default React.memo(NetworkLoadSlaTable)
